import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
} from 'react';
import { View, Text, Platform, LayoutChangeEvent } from 'react-native';
import {
  DocketStatus,
  LayoutStrategy,
  PrinterProfile,
} from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import { useScale } from '../../../hooks/useScale';
import { useDockets } from '../../../hooks/useDockets';
import { useReactiveVar } from '@apollo/client/react/hooks';
import {
  docketsUpdated,
  openDocketslastUpdatedAt,
  docketsLastMovedAt,
  allDayViewItemsVar,
  rushedVar,
} from '../../../state/apolloVars';
import {
  PaginatedDocketItems,
  getDefaultRowsPerPage,
} from '../../../utils/layoutHelper';
import { headers } from '../../../utils/fetchHeaders';
import { transformDocketToDocketDisplayItems } from '../../../utils/docketHelper/transformDockets';
import {
  DOCKET_HEIGHT,
  DOCKET_WIDTH,
  FEATURES,
  FooterHeight,
  HeaderHeight,
  SortingPreference,
  CourseStatus,
} from '../../../common/constants';
import {
  DocketDisplayItem,
  DocketDetailsProps,
  DocketDisplayItemType,
  DocketDisplayOtherItem,
} from '../../../common/types';
import MainScreenStyles from '../MainScreen.styles';
import DocketsGrid from './DocketsGrid';
import DocketHermes from './DocketHermes';
import Pagination from '../../../component/Pagination/Pagination';
import { usePrinterProfiles } from '../../../hooks/usePrinterProfiles';
import { useOrderTypes } from '../../../hooks/useOrderTypes';
import { useSalesChannels } from '../../../hooks/useSalesChannels';
import { useSession } from '../../../hooks/useSession';
import { groupDocketItems } from '../../../utils/docketHelper/groupDocketItems';
import {
  getItemCounts,
  sortDockets,
} from '../../../utils/docketHelper/sortDockets';
import { docketsCategoriesCount } from '../../../state/apolloVars';
import { analyticsService } from '../../../analytics/AnalyticsService';
import theme from '../../../common/theme';
import Svg, { Line } from 'react-native-svg';
import { getTwoRowPaginatedDocketItems } from '../../../utils/layouts/TwoRow/paginatedDocketItems';
import { getSingleRowPaginatedDocketItems } from '../../../utils/layouts/SingleRow/paginatedDocketItems';
import { getPaginatedDocketItems } from '../../../utils/layouts/FreeFlow/paginatedDocketItems';
import { consolidateDockets } from '../../../utils/docketHelper/consolidateDockets';
import { sortDocketItems } from '../../../utils/docketItemHelper/sortDocketItems';
import { useCategories } from '../../../hooks/useCategories';
import { getGridPaginatedDocketItems } from '../../../utils/layouts/Grid/paginatedDocketItems';
import {
  filterDockets as filterByCallCourseStatus,
  filterDocketsByOrderId,
} from '../../../utils/docketHelper/filterDockets';
import { computeDocketCountsByOrderId } from '../../../utils/docketHelper/docketCounting';

export interface DocketsLayoutProps {
  docketStatus: DocketStatus | string;
  courseStatus?: CourseStatus;
  headers: headers;
  setCurrentPage: (page: number) => void;
  currentPage: number;
  enablePrint?: boolean;
  onToggleDocketDetails: (a: DocketDetailsProps) => void;
  setDocketDetails: React.Dispatch<
    React.SetStateAction<DocketDetailsProps | undefined>
  >;
  selectedDocket?: string;
  closeDocketDetails?: () => void;
}

const DocketsLayout: React.FC<DocketsLayoutProps> = ({
  docketStatus,
  courseStatus,
  headers,
  setCurrentPage: setCurrentPageIndex,
  currentPage,
  enablePrint,
  onToggleDocketDetails,
  setDocketDetails,
  selectedDocket,
  closeDocketDetails,
}) => {
  const { scale } = useScale();
  const { session } = useSession();
  const styles = MainScreenStyles();
  const { translate } = useTranslation();
  const { getDockets, ackDocketsReceived } = useDockets();
  const [layoutHeight, setLayoutHeight] = useState(0);
  const docketsUpdatedVar = useReactiveVar(openDocketslastUpdatedAt);
  const docketMoved = useReactiveVar(docketsLastMovedAt);
  const previousDocketItemsRef = useRef<PaginatedDocketItems[]>([]);
  const sortingPrefRef = useRef('');
  const [docketsToShow, setDocketsToShow] = useState<DocketDisplayItem[]>([]);
  const [paginatedDocketItems, setPaginatedDocketItems] = useState<
    PaginatedDocketItems[]
  >([]);
  const { printerProfiles } = usePrinterProfiles();
  const { orderTypes } = useOrderTypes();
  const { salesChannels } = useSalesChannels();
  const { categories } = useCategories();
  const [columnHeight, setColumnHeight] = useState<number>(0);
  const [docketsReceivedAckEnabled, setDocketsReceivedAckEnabled] =
    useState(false);
  const [filteredOrderId, setFilteredOrderId] = useState<string | undefined>(
    undefined,
  );
  const pageBeforeFilterRef = useRef<number | undefined>(undefined);
  const isRushed = rushedVar();

  const handleDocketCountClick = useCallback(
    (orderId: string) => {
      setFilteredOrderId(prevOrderId => {
        if (prevOrderId === orderId) {
          // Removing filter - restore original page
          if (pageBeforeFilterRef.current !== undefined) {
            // Only reset pagination cache for DOUBLE_ROW layout which uses it
            if (
              session?.kitchenDisplay?.layoutStrategy?.strategy ===
              LayoutStrategy.DOUBLE_ROW
            ) {
              previousDocketItemsRef.current = [];
            }
            setCurrentPageIndex(pageBeforeFilterRef.current);
            pageBeforeFilterRef.current = undefined;
          }
          return undefined;
        } else {
          // Adding filter - save current page and reset to 0
          if (prevOrderId === undefined) {
            // Only reset pagination cache for DOUBLE_ROW layout which uses it
            if (
              session?.kitchenDisplay?.layoutStrategy?.strategy ===
              LayoutStrategy.DOUBLE_ROW
            ) {
              previousDocketItemsRef.current = [];
            }
            pageBeforeFilterRef.current = currentPage;
            setCurrentPageIndex(0);
          }
          return orderId;
        }
      });
    },
    [
      currentPage,
      setCurrentPageIndex,
      session?.kitchenDisplay?.layoutStrategy?.strategy,
    ],
  );

  const minimumColumnsOnScreen = useMemo(() => {
    const colsPerPage = Math.min(
      Math.floor(theme.window.vw / scale(DOCKET_WIDTH)),
      session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen ?? 8,
    );
    return colsPerPage;
  }, [session?.kitchenDisplay?.layoutStrategy, scale]);

  const minimumRowsOnScreen = useMemo(() => {
    const rowsPerPage = Math.min(
      Math.floor(theme.window.vh / scale(DOCKET_HEIGHT)),
      session?.kitchenDisplay?.layoutStrategy?.rowsOnScreen ||
        getDefaultRowsPerPage(
          session?.kitchenDisplay?.layoutStrategy?.strategy,
        ),
    );
    return rowsPerPage;
  }, [session?.kitchenDisplay?.layoutStrategy, scale]);

  const assignedProfiles = useMemo(
    () =>
      session?.kitchenDisplay?.printerProfiles?.map((x: PrinterProfile) => x) ||
      [],
    [session?.kitchenDisplay?.printerProfiles],
  );
  useEffect(() => {
    (async () => {
      const isEnabled = await analyticsService.isFeatureEnabled(
        FEATURES.DOCKETS_RECEIVED_ACK,
      );
      setDocketsReceivedAckEnabled(isEnabled);
    })();
  }, []);

  const getOrderTypeCode = useCallback(
    (id: string) => {
      return orderTypes.find(orderType => orderType?.id === id)?.code as string;
    },
    [orderTypes],
  );

  const assignedProfilesIds = useMemo(
    () =>
      Array.from(
        new Set(
          session?.kitchenDisplay?.printerProfiles?.map(
            (p: PrinterProfile) => p.id,
          ) || [],
        ),
      ),
    [session?.kitchenDisplay?.printerProfiles],
  );

  const updateDocketDetailsIfSelected = useCallback(
    (updatedDockets: DocketDisplayItem[]) => {
      if (!selectedDocket) return;

      // Find the selected docket in the updated data
      const selectedDocketData = updatedDockets.find(
        item =>
          item.type === DocketDisplayItemType.OTHER_ITEMS &&
          (item as DocketDisplayOtherItem).docketId === selectedDocket,
      ) as DocketDisplayOtherItem;

      if (selectedDocketData) {
        setDocketDetails(prev => {
          if (!prev?.orderTypeId) return prev;

          // Only update if data actually changed
          if (
            prev.items !== selectedDocketData.items ||
            prev.notifierItems !== selectedDocketData.notifierItems ||
            prev.status !== selectedDocketData.status
          ) {
            return {
              ...prev,
              items: selectedDocketData.items,
              notifierItems: selectedDocketData.notifierItems,
              status: selectedDocketData.status,
            };
          }
          return prev;
        });
      }
    },
    [selectedDocket, setDocketDetails],
  );

  useEffect(() => {
    (async () => {
      if (
        !isNaN(docketsUpdatedVar) &&
        orderTypes.length &&
        salesChannels.length &&
        categories.length
      ) {
        const dockets = await getDockets(docketStatus as DocketStatus);
        const filteredDockets = dockets.filter(x => {
          const validOrderTypes = printerProfiles.find(
            profile => profile.id === x?.printerProfileId,
          )?.orderTypes;
          if (!validOrderTypes || validOrderTypes?.length === 0) return x;
          if (validOrderTypes.includes(getOrderTypeCode(x?.orderType?.id)))
            return x;
          return;
        });

        let filteredByOrderId = filteredDockets;
        if (filteredOrderId) {
          filteredByOrderId = filterDocketsByOrderId(
            filteredOrderId,
            filteredDockets,
          );
        }

        const filteredByCourseCallStatus = filterByCallCourseStatus(
          courseStatus,
          filteredByOrderId,
          orderTypes,
        );

        if (docketStatus !== DocketStatus.COMPLETED) {
          const allDayViewData = groupDocketItems(filteredByCourseCallStatus);
          allDayViewItemsVar(allDayViewData);
        }
        const sortedDockets = sortDockets(
          session.sortingPref?.value || '',
          filteredByCourseCallStatus,
          docketStatus,
          orderTypes,
          salesChannels,
        );

        const docketCountsByOrderId =
          computeDocketCountsByOrderId(sortedDockets);
        const itemCounts = getItemCounts(sortedDockets, assignedProfilesIds);
        if (itemCounts) docketsCategoriesCount(itemCounts);
        if (
          sortingPrefRef.current !==
          (session?.sortingPref?.value ?? SortingPreference.OLDEST_FIRST)
        ) {
          previousDocketItemsRef.current = [];
          sortingPrefRef.current =
            session?.sortingPref?.value ?? SortingPreference.OLDEST_FIRST;
        }
        const docketsWithSortedItems = sortDocketItems(
          sortedDockets,
          assignedProfiles,
          categories,
          session?.kitchenDisplay?.groupItemsByStatus ?? true,
        );
        const consolidatedDockets = consolidateDockets(
          docketsWithSortedItems,
          printerProfiles,
        );
        const transformedDockets = consolidatedDockets.map(docket => {
          const orderGroup = docketCountsByOrderId.get(docket.orderId || '');
          const currentIndex = orderGroup?.indices.get(docket.id);
          const totalCount = orderGroup?.total;

          return transformDocketToDocketDisplayItems(
            docket,
            assignedProfiles,
            session,
            orderTypes,
            categories,
            currentIndex,
            totalCount,
          );
        });
        const dataToShow = transformedDockets?.flat();
        setDocketsToShow(dataToShow);
        updateDocketDetailsIfSelected(dataToShow);
        docketsReceivedAckEnabled && ackDocketsReceived(sortedDockets);
      }
    })();
  }, [
    getDockets,
    docketsUpdatedVar,
    categories,
    docketStatus,
    courseStatus,
    session,
    printerProfiles,
    getOrderTypeCode,
    session.sortingPref,
    orderTypes,
    salesChannels,
    ackDocketsReceived,
    docketsReceivedAckEnabled,
    assignedProfiles,
    assignedProfilesIds,
    updateDocketDetailsIfSelected,
    filteredOrderId,
  ]);

  const availableHeight = useMemo(() => {
    if (Platform.OS === 'web') {
      return theme.window.vh - scale(FooterHeight) - scale(HeaderHeight);
    }
    return theme.window.vh - 190;
  }, [scale]);

  const getPaginatedItems = useCallback(
    (
      strategy: LayoutStrategy | undefined,
      dockets: DocketDisplayItem[],
      scale: (n: number) => number,
      height: number,
    ) => {
      // Default to FREE_FLOW if strategy is undefined
      const layoutStrategy = strategy || LayoutStrategy.FREE_FLOW;

      switch (layoutStrategy) {
        case LayoutStrategy.DOUBLE_ROW:
          return getTwoRowPaginatedDocketItems(
            dockets,
            scale,
            height,
            previousDocketItemsRef.current,
            minimumColumnsOnScreen,
          );

        case LayoutStrategy.SINGLE_ROW:
          return getSingleRowPaginatedDocketItems(
            dockets,
            scale,
            height,
            minimumColumnsOnScreen,
          );

        case LayoutStrategy.FREE_FLOW:
          return getPaginatedDocketItems(
            dockets,
            scale,
            height,
            minimumColumnsOnScreen,
          );

        case LayoutStrategy.GRID:
          return getGridPaginatedDocketItems(
            dockets,
            scale,
            height / minimumRowsOnScreen,
            minimumColumnsOnScreen,
            minimumRowsOnScreen,
          );

        default:
          return getPaginatedDocketItems(
            dockets,
            scale,
            height,
            minimumColumnsOnScreen,
          );
      }
    },
    [minimumColumnsOnScreen, minimumRowsOnScreen],
  );

  useLayoutEffect(() => {
    if (docketsToShow.length) {
      setPaginatedDocketItems(() =>
        getPaginatedItems(
          session?.kitchenDisplay?.layoutStrategy?.strategy,
          docketsToShow,
          scale,
          layoutHeight > 0 ? layoutHeight : availableHeight,
        ),
      );
    } else {
      setPaginatedDocketItems([]);
    }
  }, [
    docketsToShow,
    scale,
    columnHeight,
    session?.kitchenDisplay?.layoutStrategy?.strategy,
    session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen,
    availableHeight,
    layoutHeight,
    session?.sortingPref?.value,
    getPaginatedItems,
  ]);

  const onChangePage = useCallback(
    (page: number) => {
      setCurrentPageIndex(page);
    },
    [setCurrentPageIndex],
  );
  useEffect(() => {
    previousDocketItemsRef.current = [];
  }, [
    session?.sortingPref?.value,
    session?.kitchenDisplay?.layoutStrategy,
    scale,
    isRushed,
    docketMoved,
  ]);

  useEffect(() => {
    onChangePage(0);
  }, [
    session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen,
    onChangePage,
    session?.kitchenDisplay?.layoutStrategy?.strategy,
  ]);

  useEffect(() => {
    if (paginatedDocketItems.length > 0) {
      previousDocketItemsRef.current = paginatedDocketItems;
    }
  }, [paginatedDocketItems]);

  useEffect(() => {
    docketsUpdated({ timestamp: Date.now() });
  }, []);

  const dividerTop =
    Platform.OS === 'android'
      ? columnHeight ?? availableHeight ?? 'auto'
      : columnHeight ?? 'auto';

  const dividerPositions = useMemo((): number[] => {
    const positions: number[] = [];
    if (typeof dividerTop === 'number') {
      for (let i = 1; i < minimumRowsOnScreen; i++) {
        /** NOTE: using scale 5 because the gap between two rows is scale(10)
         * so the divider should be placed at the middle of the gap i.e scale(5).
         */

        positions.push((i * dividerTop) / minimumRowsOnScreen - scale(5));
      }
    }
    return positions;
  }, [dividerTop, minimumRowsOnScreen, scale]);

  const onLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setLayoutHeight?.(height - scale(FooterHeight));
  };

  const shouldShowDividers = useMemo(() => {
    return (
      paginatedDocketItems.length > 0 &&
      (session?.kitchenDisplay?.layoutStrategy?.strategy ===
        LayoutStrategy.GRID ||
        session?.kitchenDisplay?.layoutStrategy?.strategy ===
          LayoutStrategy.DOUBLE_ROW)
    );
  }, [
    paginatedDocketItems.length,
    session?.kitchenDisplay?.layoutStrategy?.strategy,
  ]);

  const rowsPerPage = useMemo(() => {
    return session?.kitchenDisplay?.layoutStrategy?.strategy ===
      LayoutStrategy.GRID
      ? minimumRowsOnScreen
      : getDefaultRowsPerPage(
          session?.kitchenDisplay?.layoutStrategy?.strategy,
        );
  }, [session?.kitchenDisplay?.layoutStrategy?.strategy, minimumRowsOnScreen]);

  return (
    <View style={[styles.layout, styles.container]} onLayout={onLayout}>
      <>
        {/* Horizontal Dividers */}
        {shouldShowDividers &&
          dividerPositions.map((position, index) => (
            <View
              key={`divider-${index}`}
              style={[
                styles.divider,
                {
                  top: position,
                },
              ]}
            >
              <Svg height="4" width="100%">
                <Line
                  x1="0"
                  y1="0"
                  x2="100%"
                  y2="0"
                  stroke={theme.colors.grey4}
                  strokeWidth="4"
                  strokeDasharray="4, 4"
                />
              </Svg>
            </View>
          ))}
      </>
      {paginatedDocketItems.length > 0 ? (
        <>
          <DocketsGrid
            status={docketStatus as DocketStatus}
            displayDocketItems={paginatedDocketItems}
            currentPage={currentPage}
            enablePrint={enablePrint}
            setColumnsHeight={setColumnHeight}
            columnsHeight={columnHeight}
            onToggleDocketDetails={onToggleDocketDetails}
            selectedDocket={selectedDocket}
            closeDocketDetails={closeDocketDetails}
            rowsPerPage={rowsPerPage}
            onDocketCountClick={handleDocketCountClick}
            filteredOrderId={filteredOrderId}
          />

          <Pagination
            data={paginatedDocketItems.map(x => x.page)}
            currentPageIndex={currentPage}
            onChange={onChangePage}
          />
        </>
      ) : (
        <View style={styles.emptyView}>
          {docketStatus === DocketStatus.CREATED ? (
            <></>
          ) : (
            <Text style={styles.emptyText}>
              {translate('kitchenDisplay.emptyLayout')}
            </Text>
          )}
        </View>
      )}
      <DocketHermes
        headers={headers}
        docketStatus={docketStatus as DocketStatus}
      />
    </View>
  );
};

export default DocketsLayout;
